import {
    initializeStepperByName,
    resetQuestionnaire,
    setFirstStep,
    setNextStep,
    setPrevStep,
    stepperInitializationErrorCodeSelector,
    stepperSelector,
    StepperType,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { ComponentType, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { QuestionnairePagesType } from "../PensionQuestionnaire";
import { QuestionnaireQueryType } from "../PensionQuestionnaire/Questionnaire.types";
import { DefaultStepProps } from "./CustomStepper";
import { computeExternalBusy, ConfirmOverlay, ConfirmOverlayProps } from "./index";
import { MissingContractContextErrorStep } from "./MissingContractContextErrorStep";

type UseStepperControllerParams<P extends { contractId?: string } = { contractId?: string }> = {
    name: StepperType;
    // pages map: components that accept P plus DefaultStepProps
    // allow numeric enum keys (StepperIndexes) as well as string keys
    pages: QuestionnairePagesType<P & QuestionnaireQueryType>;
    initializeStepper?: boolean;
    queries?: QuestionnaireQueryType["queries"];
    extraProps?: P;
    onCloseConfirmed?: () => void;
};

export const useStepperController = <P extends { contractId?: string } = { contractId?: string }>({
    name,
    initializeStepper = true,
    pages,
    queries,
    extraProps,
    onCloseConfirmed,
}: UseStepperControllerParams<P>) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();

    // Note: callers often pass a stable `queries` object (via useStableLatestObject)
    // whose identity doesn't change while its inner fields do. Using useMemo here
    // prevented recomputation when only inner fields changed, so externalBusy
    // stayed stale. Compute it eagerly so we observe the latest inner state.
    const externalBusy = computeExternalBusy(queries);

    const {
        currentStepIndex: activeStepIndex,
        currentStepKey: activeStepKey,
        totalSteps,
    } = useAppSelector(stepperSelector);

    const initializationErrorCode = useAppSelector(stepperInitializationErrorCodeSelector);
    const extraContractId = (extraProps?.contractId ?? "").trim();

    // initialize stepper
    useEffect(() => {
        if (initializeStepper) {
            dispatch(
                initializeStepperByName({
                    name,
                    contractId: extraContractId,
                }),
            );
        }
    }, [dispatch, initializeStepper, name, extraContractId]);

    const hasInitializationError = initializationErrorCode !== undefined;
    const effectiveTotalSteps = hasInitializationError ? 1 : totalSteps;

    const [overlayProps, setOverlayProps] = useState<ConfirmOverlayProps | null>(null);

    const closeOverlayProps = useMemo<ConfirmOverlayProps>(
        () => ({
            title: t("investmentStrategy.warningModal.closeModal"),
            text: t("investmentStrategy.warningModal.description"),
            confirmAction: () => {
                onCloseConfirmed?.();
                setOverlayProps(null);
                dispatch(resetQuestionnaire());
            },
            cancelAction: () => setOverlayProps(null),
            confirmButtonTitle: t("investmentStrategy.warningModal.yes"),
            cancelButtonTitle: t("investmentStrategy.warningModal.no"),
        }),
        [dispatch, onCloseConfirmed, t],
    );

    const showConfirmModal = useCallback((props: ConfirmOverlayProps) => {
        setOverlayProps(props);
    }, []);

    const hideConfirmModal = useCallback(() => {
        setOverlayProps(null);
    }, []);

    const handleNext = useCallback(async () => {
        if (hasInitializationError || activeStepIndex >= effectiveTotalSteps - 1) {
            onCloseConfirmed?.();
            dispatch(setFirstStep());
            dispatch(resetQuestionnaire());
        } else {
            dispatch(setNextStep());
        }
    }, [activeStepIndex, dispatch, onCloseConfirmed, hasInitializationError, effectiveTotalSteps]);

    const handleBack = useCallback(() => {
        dispatch(setPrevStep());
    }, [dispatch]);

    // Prevent remounts of the active page when overlayProps or callers change reference
    // by keeping a stable Wrapper component and reading latest extraProps via ref.
    const extraPropsRef = useRef<P | undefined>(extraProps as P | undefined);
    useEffect(() => {
        extraPropsRef.current = extraProps as P | undefined;
    }, [extraProps]);

    const activeStep = useMemo<FC<Partial<DefaultStepProps>>>(() => {
        if (initializationErrorCode === "missingContractId") {
            const MissingContractIdErrorStep: FC<Partial<DefaultStepProps>> = () => <MissingContractContextErrorStep />;
            MissingContractIdErrorStep.displayName = "StepperPage_MissingContractIdError";
            return MissingContractIdErrorStep;
        }

        if (activeStepKey in pages && pages[activeStepKey]) {
            const Page = pages[activeStepKey] as ComponentType<P & Partial<DefaultStepProps>>;
            const Wrapper: FC<Partial<DefaultStepProps>> = (injectedProps) => {
                return (
                    <Page
                        {...(extraPropsRef.current as P)}
                        {...(injectedProps as Partial<DefaultStepProps>)}
                        queries={queries}
                    />
                );
            };
            Wrapper.displayName = `StepperPage_${String(activeStepKey)}`;
            return Wrapper;
        }
        const Empty: FC<Partial<DefaultStepProps>> = () => null;
        Empty.displayName = "StepperPage_Empty";
        return Empty;
    }, [activeStepKey, pages, queries, initializationErrorCode]);

    // Last step - change next button to "Close"
    const nextButtonText = useMemo(() => {
        if (activeStepIndex === effectiveTotalSteps - 1) {
            return t("global.close");
        }
        return t("global.continue");
    }, [activeStepIndex, t, effectiveTotalSteps]);

    const onCloseHandler = useCallback(() => {
        setOverlayProps(closeOverlayProps);
    }, [closeOverlayProps]);

    const overlayElementMemo = useMemo(() => {
        return overlayProps ? (
            <ConfirmOverlay {...overlayProps} isLoading={externalBusy} />
        ) : externalBusy ? (
            <></>
        ) : null;
    }, [overlayProps, externalBusy]);

    return {
        externalBusy,
        activeStepIndex,
        activeStepKey,
        totalSteps: effectiveTotalSteps,
        activeStep,
        overlayProps,
        overlayElement: overlayElementMemo,
        closeOverlayProps,
        showConfirmModal,
        hideConfirmModal,
        handleNext,
        handleBack,
        nextButtonText,
        onCloseHandler,
        setOverlayProps,
    };
};
